import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import DataFrameTableWrapper, {DataFrameTableWrapperProps} from 'data-frames/wrapper/DataFrameTableWrapper';

const TABLE_CARD_MARGIN = 12;

/**
 * Calculate the height of each table (as a CSS attribute) so that scrolling is only necessary with >2 tables.
 */
function calculateTableCardMaxHeight(numDataFrames: number): string {
  const heightPercent = numDataFrames > 0 ? Math.max(100 / numDataFrames, 40) : 100;
  return `calc(${heightPercent}% - ${TABLE_CARD_MARGIN}px)`;
}

export type DataFrameTableListProps = Pick<
  DataFrameTableWrapperProps,
  'rowSelectionProps' | 'renderIsExcludedColumn'
> & {
  actionOrigin: ActionOrigin.ANALYSIS_TOOL;
  dataFrames: Array<DataFrame>;
  clientStateId: string;
  defaultTableMode?: DataFrameTableMode;
};

/**
 * Render a vertical stack of `DataFrameTable`s.
 *
 * `DataFrameTableList` adjusts tables' heights to minimize unnecessary scrolling.
 */
export default function DataFrameTableList(props: DataFrameTableListProps): JSX.Element {
  const maxHeight = calculateTableCardMaxHeight(props.dataFrames.length);

  return (
    <div className={classNames(css(styles.stack), 'scrollbar-override')}>
      {props.dataFrames.map((dataFrame, index) => (
        <div className={css(styles.tableCard)} key={index} style={{maxHeight}}>
          <div className={css(styles.table)}>
            <DataFrameTableWrapper
              actionOrigin={props.actionOrigin}
              rowSelectionProps={props.rowSelectionProps}
              clientStateId={`${props.clientStateId}-${index}`}
              dataFrame={dataFrame}
              defaultTableMode={props.defaultTableMode}
              renderIsExcludedColumn={props.renderIsExcludedColumn}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  stack: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'auto',
    paddingBottom: TABLE_CARD_MARGIN / 2,
    paddingTop: TABLE_CARD_MARGIN / 2,
  },
  table: {
    height: '100%',
  },
  tableCard: {
    borderRadius: 4,
    marginBottom: TABLE_CARD_MARGIN / 2,
    marginLeft: TABLE_CARD_MARGIN,
    marginRight: TABLE_CARD_MARGIN,
    marginTop: TABLE_CARD_MARGIN / 2,
  },
});
